import { create } from "zustand";
import { produceWithPatches, enablePatches, type Patch } from "immer";
import { ulid } from "ulid";
import type { MapDocument, Feature, RoadFeature, BuildingFeature } from "@maps/schema";

enablePatches();

const MAX_UNDO = 200;

interface HistoryEntry {
  undo: Patch[];
  redo: Patch[];
}

export type Tool = "select" | "road" | "building";

export interface EditorState {
  doc: MapDocument;
  revision: number;
  tool: Tool;
  selectedId: string | null;

  past: HistoryEntry[];
  future: HistoryEntry[];

  dirty: boolean;

  setTool: (tool: Tool) => void;
  select: (id: string | null) => void;

  addRoad: (road: Omit<RoadFeature, "id" | "type" | "props">) => void;
  addBuilding: (building: Omit<BuildingFeature, "id" | "type" | "props">) => void;
  deleteFeature: (id: string) => void;
  updateFeature: (id: string, updater: (f: Feature) => Feature) => void;

  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  setDoc: (doc: MapDocument, revision: number) => void;
  markSaved: () => void;
}

function applyPatches(doc: MapDocument, patches: Patch[]): MapDocument {
  return immerApplyPatches(doc, patches);
}

// Re-export from immer for applyPatches
import { applyPatches as immerApplyPatches } from "immer";

function commit(state: EditorState, nextDoc: MapDocument, undo: Patch[], redo: Patch[]): Partial<EditorState> {
  const past = [...state.past, { undo, redo }];
  if (past.length > MAX_UNDO) past.shift();
  return {
    doc: nextDoc,
    past,
    future: [],
    dirty: true,
  };
}

export function createDoc(name = "Untitled Map"): MapDocument {
  return {
    formatVersion: 1,
    id: ulid(),
    name,
    revision: 0,
    features: [],
  };
}

export const useEditor = create<EditorState>((set, get) => ({
  doc: createDoc(),
  revision: 0,
  tool: "select",
  selectedId: null,
  past: [],
  future: [],
  dirty: false,

  setTool: (tool) => set({ tool, selectedId: null }),
  select: (id) => set({ selectedId: id }),

  addRoad: (road) => {
    const feature: RoadFeature = {
      ...road,
      id: ulid(),
      type: "road",
      props: {},
    };
    const [nextDoc, patches] = produceWithPatches(get().doc, (draft) => {
      draft.features.push(feature);
    });
    set((state) => commit(state, nextDoc, patches, []));
  },

  addBuilding: (building) => {
    const feature: BuildingFeature = {
      ...building,
      id: ulid(),
      type: "building",
      props: {},
    };
    const [nextDoc, patches] = produceWithPatches(get().doc, (draft) => {
      draft.features.push(feature);
    });
    set((state) => commit(state, nextDoc, patches, []));
  },

  deleteFeature: (id) => {
    const [nextDoc, patches] = produceWithPatches(get().doc, (draft) => {
      const idx = draft.features.findIndex((f) => f.id === id);
      if (idx >= 0) draft.features.splice(idx, 1);
    });
    set((state) => ({
      ...commit(state, nextDoc, patches, []),
      selectedId: null,
    }));
  },

  updateFeature: (id, updater) => {
    const [nextDoc, patches] = produceWithPatches(get().doc, (draft) => {
      const idx = draft.features.findIndex((f) => f.id === id);
      if (idx >= 0) {
        const existing = draft.features[idx];
        if (existing) draft.features[idx] = updater(existing);
      }
    });
    set((state) => commit(state, nextDoc, patches, []));
  },

  undo: () => {
    const { past, future, doc } = get();
    if (past.length === 0) return;
    const entry = past[past.length - 1]!;
    const prevDoc = applyPatches(doc, entry.undo);
    set({
      doc: prevDoc,
      past: past.slice(0, -1),
      future: [entry, ...future],
      dirty: true,
      selectedId: null,
    });
  },

  redo: () => {
    const { past, future, doc } = get();
    if (future.length === 0) return;
    const entry = future[0]!;
    const nextDoc = applyPatches(doc, entry.redo);
    set({
      doc: nextDoc,
      past: [...past, entry],
      future: future.slice(1),
      dirty: true,
      selectedId: null,
    });
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  setDoc: (doc, revision) =>
    set({ doc, revision, past: [], future: [], dirty: false, selectedId: null }),

  markSaved: () => set({ dirty: false }),
}));